import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import { listProductTagScanJobs } from "@/api/productTags.js";

const POLL_MS = 2500;
const ACTIVE = new Set(["queued", "processing"]);

function statusBadgeClass(status) {
  switch (status) {
    case "queued":
      return "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
    case "processing":
      return "bg-sky-100 text-sky-700 dark:bg-sky-400/10 dark:text-sky-300";
    case "completed":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300";
    case "partial":
      return "bg-amber-100 text-amber-800 dark:bg-amber-400/10 dark:text-amber-300";
    case "failed":
      return "bg-rose-100 text-rose-700 dark:bg-rose-400/10 dark:text-rose-300";
    default:
      return "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300";
  }
}

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function jobTarget(job) {
  if (job?.mode === "tag_scan") {
    return job.tag_id || "—";
  }
  if (job?.mode === "product_scan") {
    return job.product_sku_id || "—";
  }
  return "—";
}

function progressLabel(job) {
  const total = Number(job?.total) || 0;
  const processed = Number(job?.processed) || 0;
  if (!total && !processed) return "—";
  return `${processed}/${total}`;
}

export default function TagScanJobsModal({ open, setOpen, initialTagId = "" }) {
  const dispatch = useDispatch();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tagFilter, setTagFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (open) {
      setTagFilter(initialTagId || "");
    }
  }, [open, initialTagId]);

  const loadJobs = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const data = await listProductTagScanJobs({
        tag_id: tagFilter.trim() || undefined,
        status: statusFilter || undefined,
        limit: 50,
      });
      setJobs(Array.isArray(data) ? data : []);
    } catch (error) {
      if (!quiet) {
        dispatch(addToast({
          message: error.response?.data?.message || "Failed to load scan jobs",
          type: "error",
        }));
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [dispatch, tagFilter, statusFilter]);

  useEffect(() => {
    if (!open) return;
    loadJobs();
  }, [open, loadJobs]);

  const hasActive = useMemo(
    () => jobs.some((j) => ACTIVE.has(j.status)),
    [jobs]
  );

  useEffect(() => {
    if (!open || !hasActive) return undefined;
    const id = setInterval(() => {
      loadJobs({ quiet: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [open, hasActive, loadJobs]);

  return (
    <ModalBasic
      id="product-tag-scan-jobs"
      title="Tag scan jobs"
      modalOpen={open}
      setModalOpen={setOpen}
      width="4xl"
    >
      <div className="px-5 py-4 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium mb-1" htmlFor="scan-jobs-tag-filter">
              Tag ID
            </label>
            <input
              id="scan-jobs-tag-filter"
              type="text"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value.toUpperCase())}
              className="form-input w-40 uppercase"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" htmlFor="scan-jobs-status-filter">
              Status
            </label>
            <select
              id="scan-jobs-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select w-40"
            >
              <option value="">All</option>
              <option value="queued">queued</option>
              <option value="processing">processing</option>
              <option value="completed">completed</option>
              <option value="partial">partial</option>
              <option value="failed">failed</option>
            </select>
          </div>
          <SecondaryButton onClick={() => loadJobs()} disabled={loading}>
            Refresh
          </SecondaryButton>
          {hasActive ? (
            <span className="text-xs text-slate-500 dark:text-slate-400 self-center">
              Auto-refreshing while jobs are active…
            </span>
          ) : null}
        </div>

        <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded">
          <table className="table-auto w-full text-sm dark:text-slate-300">
            <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/30">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Mode</th>
                <th className="px-3 py-2 text-left font-semibold">Target</th>
                <th className="px-3 py-2 text-left font-semibold">Progress</th>
                <th className="px-3 py-2 text-left font-semibold">Matched</th>
                <th className="px-3 py-2 text-left font-semibold">Removed</th>
                <th className="px-3 py-2 text-left font-semibold">Failed</th>
                <th className="px-3 py-2 text-left font-semibold">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading && jobs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center text-slate-500">
                    Loading jobs…
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-8 text-center text-slate-500">
                    No scan jobs found.
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr key={job.id}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(job.status)}`}
                      >
                        {job.status}
                      </span>
                      {job.error_message ? (
                        <div className="text-xs text-rose-500 mt-1 max-w-xs truncate" title={job.error_message}>
                          {job.error_message}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                      {job.mode}
                      {job.reverify ? (
                        <span className="ml-1 text-slate-400">(reverify)</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium">
                      {jobTarget(job)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{progressLabel(job)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{job.matched ?? 0}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{job.removed ?? 0}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{job.failed ?? 0}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">
                      {formatWhen(job.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end">
        <PrimaryButton onClick={() => setOpen(false)}>Close</PrimaryButton>
      </div>
    </ModalBasic>
  );
}
